// src/utils/lessonProjection.js
//
// Pass 85.1A — SAFE STUDENT-FACING LESSON PROJECTION (pure domain; no Firestore, no React).
//
// The canonical `lessonInstances` document is the SOURCE OF TRUTH and is NEVER student-readable in full (it carries
// commercial lineage, planning/assignment metadata, internal coaching drafts, unpublished AI). A lesson becomes
// visible to a linked coaching PARTICIPANT only when an operator / the current instructor takes an EXPLICIT publish
// action that copies ONLY approved, student-safe fields into a per-participant PROJECTION document.
//
//   • id = `org::lessonId::participantId` — deterministic, per-participant (one lesson → many participants, never
//     duplicating the lesson; each linked participant gets their own safe projection, so publishing to P1 never
//     leaks anything to P2).
//   • This is PUBLICATION / OUTPUT, not a competing editable lesson-report source. Publishing is idempotent.
//   • No automatic publication: not on delivery, not on closeout, not when a date passes, not when AI generates text,
//     not when a participant is linked. Only an explicit human action.

const str = (v) => String(v == null ? '' : v).trim();
const MAX_TEXT = 4000;
const MAX_LIST = 12;

// The ONLY fields a publisher may expose to a student. Anything else is dropped (and, when passed as a raw payload,
// rejected) so an internal/commercial/planning/draft field can NEVER reach the projection even by mistake.
export const SAFE_LESSON_PROJECTION_FIELDS = Object.freeze([
  'title',        // short lesson title / label
  'lessonDate',   // human date string of the session (NOT a booking/commercial record)
  'discipline',   // ski / snowboard / etc.
  'summary',      // the shared coaching summary the instructor explicitly approves for the student
  'focusAreas',   // array of short focus strings
  'nextFocus',    // one-line "what to work on next"
]);

// Fields that must NEVER appear on a projection (commercial / staff-CRM / planning / internal / assignment / draft).
export const LESSON_PROJECTION_FORBIDDEN_FIELDS = Object.freeze([
  'price', 'amount', 'revenue', 'cost', 'payment', 'paymentState', 'bookingId', 'clientId', 'requesterUserId',
  'instructorId', 'instructorAuthUserId', 'assignedInstructorId', 'assignedTo', 'stages', 'plannedDrills',
  'plannedDrillIds', 'lessonOutcome', 'skillConfidence', 'aiReview', 'aiDraft', 'notesInternal', 'studentEmail',
  'studentEmailNormalized', 'studentId', 'sourceCustomerId', 'commercial', 'internal',
]);

export function lessonProjectionId(organizationId, lessonId, participantId) {
  const org = str(organizationId); const l = str(lessonId); const p = str(participantId);
  if (!org || !l || !p) return '';
  return `${org}::${l}::${p}`;
}

// Reject any attempt to place a forbidden (commercial/internal/planning/draft) field on a projection payload.
export function assertSafeProjectionFields(payload = {}) {
  for (const k of Object.keys(payload)) {
    if (LESSON_PROJECTION_FORBIDDEN_FIELDS.includes(k)) {
      const e = new Error(`Field "${k}" is not allowed on a student lesson projection (internal/commercial/planning data is forbidden here).`);
      e.code = 'LESSON_PROJECTION_FORBIDDEN_FIELD';
      throw e;
    }
  }
  return true;
}

// Keep ONLY the whitelisted safe fields, normalized. Unknown keys are dropped; forbidden keys are rejected first.
export function pickSafeProjectionFields(safeFields = {}) {
  assertSafeProjectionFields(safeFields);
  const out = {};
  for (const k of SAFE_LESSON_PROJECTION_FIELDS) {
    if (!(k in safeFields)) continue;
    const v = safeFields[k];
    if (k === 'focusAreas') {
      const list = Array.isArray(v) ? v : [];
      out.focusAreas = list.map((x) => str(x)).filter(Boolean).slice(0, MAX_LIST);
    } else {
      out[k] = str(v).slice(0, MAX_TEXT);
    }
  }
  return out;
}

// The canonical write shape for a lesson projection. `sourceLessonUpdatedAt` records the source lineage snapshot the
// publisher approved (lets the UI show "published from this version" and keeps publication idempotent/auditable).
export function buildLessonProjection(plan = {}) {
  const organizationId = str(plan.organizationId);
  const lessonId = str(plan.lessonId);
  const participantId = str(plan.participantId);
  const safe = pickSafeProjectionFields(plan.safeFields || {});
  return {
    organizationId,
    lessonId,
    participantId,
    ...safe,
    publishedByAuthUserId: str(plan.publishedByAuthUserId),
    sourceLessonUpdatedAt: str(plan.sourceLessonUpdatedAt),
  };
}
